/**
 * Module-level singleton session state for own-vehicle cold-chain breach alert
 * de-duplication (spec A4/A6). This lives outside React so notification
 * suppression survives Cold Chain screen remounts and carousel navigation
 * for the current driver session; it is reset on own-vehicle change, driver
 * change, or logout.
 *
 * State is keyed per `${userId}:${deviceId}` and stored in a map (rather than
 * a single global slot) so that Drive's multi-driver/co-driver support does
 * not let one driver's logout or own-vehicle change wipe another concurrently
 * active driver's baseline/dedup state — only the entries for the affected
 * user are ever touched.
 *
 * IMPORTANT: `trackBreachPoll` must only be called after a SUCCESSFUL breach
 * fetch. A failed fetch must not touch this state (spec R2) — the caller is
 * responsible for skipping the call entirely when the fetch rejects.
 */

interface ITrackerState {
    baselineEstablished: boolean;
    knownActiveEventIds: Set<string>;
}

const stateByKey = new Map<string, ITrackerState>();

function toKey(userId: string, deviceId: string): string {
    return `${ userId }:${ deviceId }`;
}

function toUserPrefix(userId: string): string {
    return `${ userId }:`;
}

/**
 * Records a successful breach poll for the given user/device and returns the
 * event IDs that are newly active (i.e. should trigger a notification).
 *
 * - A change of own vehicle for the same user re-arms: any other tracked
 *   device for that user is dropped and the new device/user pair starts a
 *   fresh silent baseline. A different user's tracked state is untouched.
 * - The first poll for a key establishes a silent baseline and returns [].
 * - Subsequent polls return only IDs not already known, then replace the
 *   known set with the current IDs so a confirmed recovery (an ID no longer
 *   present) followed by a later re-breach notifies again.
 */
export function trackBreachPoll(userId: string, deviceId: string, activeEventIds: string[]): string[] {
    const key = toKey(userId, deviceId);

    let state = stateByKey.get(key);
    if (!state) {
        // Own-vehicle change for this user: drop this user's stale entry for the
        // previous device so it doesn't linger indefinitely (spec A6 re-arm).
        const prefix = toUserPrefix(userId);
        for (const existingKey of Array.from(stateByKey.keys())) {
            if (existingKey !== key && existingKey.startsWith(prefix)) {
                stateByKey.delete(existingKey);
            }
        }
        state = { baselineEstablished: false, knownActiveEventIds: new Set<string>() };
        stateByKey.set(key, state);
    }

    const trackedState = state;

    if (!trackedState.baselineEstablished) {
        trackedState.baselineEstablished = true;
        trackedState.knownActiveEventIds = new Set(activeEventIds);
        return [];
    }

    const newIds = activeEventIds.filter(id => !trackedState.knownActiveEventIds.has(id));
    trackedState.knownActiveEventIds = new Set(activeEventIds);
    return newIds;
}

/**
 * Clears tracked state for `userId` only (all of that user's tracked
 * devices), or all tracked state for every user when called with no argument.
 * Call the scoped form on a single driver's logout so a co-driver's
 * concurrently active session is unaffected; call the unscoped form for full
 * teardown (e.g. test setup/teardown).
 */
export function resetColdChainAlertTracker(userId?: string): void {
    if (userId === undefined) {
        stateByKey.clear();
        return;
    }
    const prefix = toUserPrefix(userId);
    for (const key of Array.from(stateByKey.keys())) {
        if (key.startsWith(prefix)) {
            stateByKey.delete(key);
        }
    }
}
